import moment from 'moment';
import 'moment/locale/vi';

moment.locale('vi');

export interface IPrintTicketData {
    id: string;
    code: string;
    studentName: string;
    studentUserInternalId: string;
    ticketTypeName: string;
    ticketTypeCode: string;
    price: number;
    createdByName: string;
    creationDate: string;
    expireDate: string;
    qrCode?: string;
    isUsed?: boolean;
    note?: string;
}

class BrowserPrinterService {
    /**
     * Format ngày giờ
     */
    private formatDate(dateString: string): string {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    }

    /**
     * Format tiền tệ VND
     */
    private formatCurrency(amount: number): string {
        return new Intl.NumberFormat('vi-VN').format(amount) + ' VND';
    }

    /**
     * In phiếu sử dụng cửa sổ in của trình duyệt
     * @param ticketData Dữ liệu phiếu cần in
     */
    printTicket(ticketData: IPrintTicketData): void {
        const printWindow = window.open('', '_blank', 'width=800,height=600');

        if (!printWindow) {
            throw new Error('Không thể mở cửa sổ in. Vui lòng kiểm tra trình duyệt có chặn popup không.');
        }

        const htmlContent = this.generateTicketHTML(ticketData);

        printWindow.document.write(htmlContent);
        printWindow.document.close();

        // Đợi nội dung load xong rồi mới in
        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.focus();
                printWindow.print();
                // Tự động đóng cửa sổ sau khi in hoặc hủy
                printWindow.onafterprint = () => {
                    printWindow.close();
                };
            }, 250);
        };
    }

    /**
     * Tạo HTML cho mẫu in phiếu - Giống y chang qzTrayPrinter
     * @param ticketData Dữ liệu phiếu
     * @returns HTML string
     */
    private generateTicketHTML(ticketData: IPrintTicketData): string {
        const priceFormatted = this.formatCurrency(ticketData.price);
        const creationDate = this.formatDate(ticketData.creationDate);
        const expireDate = this.formatDate(ticketData.expireDate);

        return `
<!DOCTYPE html>
<html>
<head>
    <meta charset='UTF-8'>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        body {
            width: 80mm;
            font-family: 'Arial', sans-serif;
            padding: 5mm 3mm;
            font-size: 10pt;
            line-height: 1.3;
        }
        .center {
            text-align: center;
        }
        .bold {
            font-weight: bold;
        }
        .header {
            margin-bottom: 3mm;
        }
        .header-title {
            font-size: 13pt;
            font-weight: bold;
            line-height: 1.2;
        }
        .header-address {
            font-size: 9pt;
            margin-top: 1mm;
            line-height: 1.2;
        }
        .divider {
            border-top: 1px dashed #000;
            margin: 3mm 0;
        }
        .ticket-type {
            font-size: 16pt;
            font-weight: bold;
            text-transform: uppercase;
            margin: 3mm 0;
        }
        .price-row {
            font-size: 10pt;
            margin: 0;
        }
        .price-value {
            font-size: 12pt;
            font-weight: bold;
        }
        .code-section {
            margin: 1.5mm 0;
        }
        .ticket-code {
            font-size: 12pt;
            font-weight: bold;
            margin-top: 3mm;
            letter-spacing: 1px;
        }
        .qr-code {
            width: 140px;
            height: 140px;
            margin: 0 auto;
            display: block;
        }
        .info-section {
            margin: 3mm 0;
            font-size: 9pt;
        }
        .info-row {
            margin: 1mm 0;
        }
        .info-label {
            font-weight: bold;
            display: inline-block;
            min-width: 85px;
        }
        .footer {
            margin-top: 3mm;
            font-size: 9pt;
        }
        .footer-thanks {
            font-weight: bold;
            margin-bottom: 1mm;
        }
        .footer-note {
            font-size: 8pt;
            font-style: italic;
        }
        .used-stamp {
            padding: 3mm 5mm;
            color: black;
            font-size: 14pt;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 2px;
            border: 3px solid black;
            display: inline-block;
        }
        @media print {
            body {
                width: 80mm;
            }
            @page {
                size: 80mm auto;
                margin: 0;
            }
        }
    </style>
</head>
<body>
    <!-- Header -->
    <div class='header center'>
        <div class='header-title'>TRƯỜNG CAO ĐẲNG TÂY ĐÔ</div>
        <div class='header-address'>90 Quốc lộ 1, Khu vực 2, P. Cái Răng, TP Cần Thơ</div>
    </div>
    
    <div class='divider'></div>
    
    <!-- Ticket Type -->
    <div class='ticket-type center'>${ticketData.ticketTypeName.toUpperCase()}</div>
    
    <div class='divider'></div>
    
    <!-- Code & QR -->
    <div class='code-section center'>
        <div class='ticket-code'>${ticketData.code}</div>
        ${ticketData.qrCode && ticketData.qrCode.trim() !== '' ? `
        <img src='${ticketData.qrCode}' class='qr-code' alt='QR Code' />
        ` : ''}
    </div>
    
    <!-- Price -->
    <div class='price-row center'>
        <span class='price-value'>${priceFormatted}</span>
    </div>
    
    <div class='divider'></div>
    
    <!-- Info -->
    <div class='info-section'>
        <div class='info-row'>
            <span class='info-label'>Người mua:</span>
            <span>${ticketData.studentName}</span>
        </div>
        <div class='info-row'>
            <span class='info-label'>Ngày mua:</span>
            <span>${creationDate}</span>
        </div>
        <div class='info-row'>
            <span class='info-label'>Hết hạn:</span>
            <span>${expireDate}</span>
        </div>
        <div class='info-row'>
            <span class='info-label'>Người bán:</span>
            <span>${ticketData.createdByName}</span>
        </div>
    </div>
    
    
    <!-- Footer -->
    ${!ticketData.isUsed ? `
    <div class='divider'></div>
    <div class='footer center'>
        <div class='footer-thanks'>Cám ơn quý khách</div>
        <div class='footer-note'>Vui lòng giữ phiếu để sử dụng</div>
    </div>
    ` : ''}
    

    ${ticketData.isUsed ? `
    <div class='divider'></div>
    <div class='center'>
        <div class='used-stamp'>ĐÃ SỬ DỤNG</div>
    </div>
    ` : ''}
</body>
</html>
        `;
    }

    /**
     * In nhiều phiếu cùng lúc
     * @param ticketsData Mảng dữ liệu các phiếu cần in
     */
    printMultipleTickets(ticketsData: IPrintTicketData[]): void {
        const printWindow = window.open('', '_blank', 'width=800,height=600');

        if (!printWindow) {
            throw new Error('Không thể mở cửa sổ in. Vui lòng kiểm tra trình duyệt có chặn popup không.');
        }

        const htmlContent = this.generateMultipleTicketsHTML(ticketsData);

        printWindow.document.write(htmlContent);
        printWindow.document.close();

        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.focus();
                printWindow.print();
                printWindow.onafterprint = () => {
                    printWindow.close();
                };
            }, 250);
        };
    }

    /**
     * Tạo HTML cho nhiều phiếu
     * @param ticketsData Mảng dữ liệu phiếu
     * @returns HTML string
     */
    private generateMultipleTicketsHTML(ticketsData: IPrintTicketData[]): string {
        const ticketsHTML = ticketsData.map((ticket, index) => {
            const singleTicketHTML = this.generateTicketHTML(ticket);
            // Extract body content
            const bodyMatch = singleTicketHTML.match(/<body[^>]*>([\s\S]*)<\/body>/);
            const content = bodyMatch ? bodyMatch[1] : '';

            return `
                ${content}
                ${index < ticketsData.length - 1 ? '<div style="page-break-after: always;"></div>' : ''}
            `;
        }).join('');

        // Get styles from first ticket
        const firstTicketHTML = this.generateTicketHTML(ticketsData[0]);
        const styleMatch = firstTicketHTML.match(/<style[^>]*>([\s\S]*)<\/style>/);
        const styles = styleMatch ? styleMatch[1] : '';

        return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>In Nhiều Phiếu</title>
    <style>
        ${styles}
    </style>
</head>
<body>
    ${ticketsHTML}
</body>
</html>
        `;
    }
}

const browserPrinterService = new BrowserPrinterService();
export default browserPrinterService;

